import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FilterOption, Option } from '../../models/option.interface';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Module } from '../../enums/module.enum';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  take,
  skip,
} from 'rxjs';
import {
  ActiveFilter,
  SearchService,
} from 'src/app/core/services/search.service';
import { HttpService } from 'src/app/core/services/api/http.service';
import { ApiResponse } from '../../models/api-response.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError } from 'rxjs/operators';
import { FilterService } from "primeng/api";

export type SearchEvent = Record<string, string>;

@UntilDestroy()
@Component({
  selector: 'app-table-filter-bar',
  templateUrl: './table-filter-bar.component.html',
  styleUrls: ['./table-filter-bar.component.scss'],
})
export class TableFilterBarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() shouldSendCategoriesInSeparateArray: boolean = false;
  @Input() module!: Module;
  @Input() searchByOptions: Option[] = [];
  @Input() filterByOptions: FilterOption[] = [];
  @Input() sortByOptions: Option[] = [];
  @Input() basicParams?: Record<string, any>;
  @Input() globalSearchParamName: string = 'global_search';
  filterForm!: FormGroup<Record<string, FormControl<any>>>;
  protected searchBy?: string;
  protected searchText?: string;
  protected sortBy?: string;
  private searchChanged$ = new BehaviorSubject<string | undefined>('');
  private searchSubject$ = new BehaviorSubject<{
    filter: string;
    index: number;
    page?: number;
    append?: boolean;
  }>({} as any);
  private autocompletePaging: Record<number, {
    query: string;
    page: number;
    lastPage: number;
    loading: boolean;
  }> = {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public searchService: SearchService,
    private http: HttpService,
    private filterService: FilterService
  ) {
    // Register "alwaysTrue" filter (will always return true when user type smth), we need it for api-autocomplete!
    this.filterService.register('alwaysTrue', () => true);
  }

  get activeFilters(): ActiveFilter[] {
    return this.searchService.activeFilters.filter(
      (activeFilter) => !this.basicParams?.[activeFilter.keys[0]]
    );
  }

  ngOnInit(): void {
    if (!!this.filterByOptions?.length) {
      this.initFilterForm();
    }
    this.subscribeOnSearchChange();
    this.syncStateFromParams(true);
    this.subscribeOnSearchValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['basicParams'] && !changes['basicParams'].firstChange) {
      this.syncStateFromParams(false);
    }
  }

  subscribeOnSearchValue() {
    this.searchSubject$
      .pipe(
        debounceTime(1000),
        switchMap(({ filter, index, page = 1, append = false }) => {
          if (!this.filterByOptions?.[index]?.autocompleteUrl) {
            return EMPTY;
          }
          this.filterByOptions[index].isLoading = true;
          const option = this.filterByOptions[index];
          if (option.isPaginated) {
            this.autocompletePaging[index] = {
              query: filter || '',
              page,
              lastPage: this.autocompletePaging[index]?.lastPage || 1,
              loading: true,
            };
          }
          const requestParams: Record<string, any> = {
            [option.searchCategory ?? this.globalSearchParamName]: filter ?? '',
          };
          if (option.isPaginated) requestParams['page'] = page;
          return this.http
            .getReq(option.autocompleteUrl as string, {
              params: requestParams,
            })
            .pipe(
              take(1), // Ensure one result is taken
              catchError(() => of({ data: [] })),
              finalize(() => {
                option.isLoading = false;
                if (this.autocompletePaging[index]) this.autocompletePaging[index].loading = false;
              }),
              map((response: any) => ({ response, index, page, append, filter }))
            );
        })
      )
      .subscribe(({ response, index, page, append, filter }: any) => {
        const option = this.filterByOptions[index];
        const responseData = response?.data?.data ?? response?.data ?? [];
        const incoming = option.successCallback?.(responseData) || responseData || [];
        if (option.isPaginated) {
          const meta = response?.meta || response?.data?.meta || response?.data || {};
          const lastPage = Number(meta.last_page ?? meta.total_pages ?? (meta.next_page_url ? page + 1 : page));
          this.autocompletePaging[index] = { query: filter || '', page, lastPage, loading: false };
          const current = append ? (option.options || []) : [];
          const valueKey = option.optionValue || 'id';
          option.options = [...current, ...incoming].filter((item: any, itemIndex: number, all: any[]) =>
            all.findIndex((candidate: any) => candidate?.[valueKey] === item?.[valueKey]) === itemIndex
          );
        } else {
          option.options = incoming;
        }
      });
  }

  getArrayFilterValue(value: string[] | number[]): string {
    return value
      .map((item: string | number) =>
        !Number.isNaN(Number(item)) ? Number(item) : item
      )
      .join(',');
  }

  resetFilters(): void {
    this.filterForm?.reset();
    this.setParamsToRoute();
  }

  resetAllFilters() {
    this.searchBy = undefined;
    this.searchText = undefined;
    this.sortBy = undefined;
    this.searchChanged$.next(this.searchText);
    this.resetFilters();
    setTimeout(() => {
      this.syncStateFromParams(false);
    });
  }

  removeActiveFilter(filter: ActiveFilter) {
    if (filter?.keys?.includes('search_category')) {
      this.searchBy = undefined;
    }
    if (filter?.keys?.includes('search_value')) {
      this.searchText = undefined;
    }
    if (
      filter?.keys?.includes('global_search') ||
      filter?.keys?.find((k) => k.includes('search_by'))
    ) {
      this.searchBy = undefined;
      this.searchText = undefined;
    }
    if (filter?.keys?.includes('sort')) {
      this.sortBy = undefined;
    }
    filter?.keys.forEach((key: string) => {
      this.filterForm?.get(key)?.patchValue(undefined);
    });
    this.searchService.removeActiveFilter(filter);
  }

  setParamsToRoute(resetPage: boolean = true): void {
    this.searchService.setParamsToRoute(
      this.searchBy,
      this.searchText,
      this.sortBy,
      this.getNormalizedFilterFormValue(),
      this.basicParams,
      this.shouldSendCategoriesInSeparateArray,
      resetPage
    );
  }

  applyFilters(filterOverlay: { toggle: (event: Event) => void }, event: Event): void {
    if (this.hasInvalidNumberRange) {
      return;
    }
    this.setParamsToRoute();
    filterOverlay.toggle(event);
  }

  get hasInvalidNumberRange(): boolean {
    return this.filterByOptions.some((option) => this.isNumberRangeInvalid(option));
  }

  isNumberRangeInvalid(filterOption: FilterOption): boolean {
    if (!filterOption.isNumberRange) {
      return false;
    }
    const min = this.parseNumberRangeValue(
      this.filterForm?.get(this.getNumberRangeMinKey(filterOption))?.value
    );
    const max = this.parseNumberRangeValue(
      this.filterForm?.get(this.getNumberRangeMaxKey(filterOption))?.value
    );
    return min !== null && max !== null && min > max;
  }

  handleAutocompleteSearch(
    { filter }: { filter: string },
    index: number
  ): void {
    if (!this.filterByOptions[index].autocompleteUrl) return;
    if (this.filterByOptions[index].isPaginated) this.filterByOptions[index].options = [];
    this.searchSubject$.next({ filter, index, page: 1, append: false });
  }

  handleSearchChanged(): void {
    this.searchChanged$.next(this.searchText);
  }

  getFilterBy(index: number): string {
    if (Array.isArray(this.filterByOptions[index].optionLabel)) {
      return (this.filterByOptions[index].optionLabel as Array<string>).join(
        ','
      );
    } else {
      return this.filterByOptions[index].optionLabel as string;
    }
  }

  getOptionLabel(option: any, index: number): string {
    if (Array.isArray(this.filterByOptions[index].optionLabel)) {
      const optionLabelArray = (
        this.filterByOptions[index].optionLabel as Array<string>
      ).map((labelName: string) => {
        const splitLabelName: string[] = labelName.split('.');

        return splitLabelName.reduce((result, fieldName: string) => {
          return result[fieldName];
        }, option)
      });

      return optionLabelArray.join(this.filterByOptions[index].optionLabelSeparator || ' ');
    } else {
      return option[
      (this.filterByOptions[index]?.optionLabel as string) ?? 'name'
        ];
    }
  }

  handleAutocompleteFocus(index: number): void {
    if (!this.filterByOptions[index].options?.length) {
      this.handleAutocompleteSearch({ filter: '' }, index);
    }
  }

  handleAutocompletePanelShow(index: number): void {
    this.handleAutocompleteFocus(index);
    if (!this.filterByOptions[index]?.isPaginated) return;
    setTimeout(() => {
      const panels = document.querySelectorAll<HTMLElement>('.p-dropdown-panel .p-dropdown-items-wrapper');
      const scrollElement = panels[panels.length - 1];
      if (!scrollElement) return;
      const previousHandler = (scrollElement as any).__tableFilterScrollHandler;
      if (previousHandler) scrollElement.removeEventListener('scroll', previousHandler);
      const handler = () => {
        if (scrollElement.scrollTop + scrollElement.clientHeight < scrollElement.scrollHeight - 32) return;
        this.loadNextAutocompletePage(index);
      };
      (scrollElement as any).__tableFilterScrollHandler = handler;
      scrollElement.addEventListener('scroll', handler, { passive: true });
    });
  }

  private loadNextAutocompletePage(index: number): void {
    const state = this.autocompletePaging[index];
    if (!state || state.loading || state.page >= state.lastPage) return;
    this.searchSubject$.next({
      filter: state.query,
      index,
      page: state.page + 1,
      append: true,
    });
  }

  private initFilterForm(): void {
    const filterFormObject = {};
    this.filterByOptions.forEach((filterOption: FilterOption) => {
      if (filterOption.isNumberRange) {
        Object.assign(filterFormObject, {
          [this.getNumberRangeMinKey(filterOption)]: [null],
          [this.getNumberRangeMaxKey(filterOption)]: [null],
        });
        return;
      }
      Object.assign(filterFormObject, {
        [filterOption.key]: [
          filterOption.isCheckbox || filterOption.isMultiSelect ? [] : null,
        ],
      });
    });
    this.filterForm = this.fb.group(filterFormObject);
  }

  private subscribeOnSearchChange(): void {
    this.searchChanged$
      .pipe(skip(1), debounceTime(1000), distinctUntilChanged(), untilDestroyed(this))
      .subscribe(() => this.setParamsToRoute());
  }

  private syncStateFromParams(shouldNavigate: boolean): void {
    this.route.queryParams.pipe(take(1)).subscribe((params: Params) => {
      // Reset search state before reading from params to avoid stale values persisting across tab switches
      this.searchBy = undefined;
      this.searchText = undefined;
      this.sortBy = undefined;

      //patch search text and search by category
      if (this.searchByOptions?.length) {
        this.searchByOptions.forEach((searchOption: Option) => {
          if (!this.shouldSendCategoriesInSeparateArray) {
            if (searchOption.key in params) {
              this.searchBy = searchOption.key;
              this.searchText = params[searchOption.key];
            } else if (this.globalSearchParamName in params) {
              this.searchBy = this.globalSearchParamName;
              this.searchText = params[this.globalSearchParamName];
            }
          } else {
            if ('search_category' in params) {
              this.searchBy = params['search_category'];
              this.searchText = params['search_value'];
            }
          }
        });
      } else {
        this.searchBy = this.globalSearchParamName;
        this.searchText = params[this.globalSearchParamName];
      }
      //patch filter by option
      this.filterByOptions.forEach((filterOption: FilterOption) => {
        if (filterOption.isNumberRange) {
          const minKey = this.getNumberRangeMinKey(filterOption);
          const maxKey = this.getNumberRangeMaxKey(filterOption);
          this.filterForm
            ?.get(minKey)
            ?.setValue(minKey in params ? params[minKey] : null);
          this.filterForm
            ?.get(maxKey)
            ?.setValue(maxKey in params ? params[maxKey] : null);
          return;
        }
        if (filterOption.key in params) {
          let value = params[filterOption.key];
          if (filterOption.isDateRange) {
            value = this.parseDateRange(params[filterOption.key]);
          } else if (filterOption.isDate) {
            value = new Date(params[filterOption.key]);
          } else if (filterOption.isMultiSelect) {
            value = String(params[filterOption.key])
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);
          } else {
            value = !Number.isNaN(Number(params[filterOption.key]))
              ? Number(params[filterOption.key])
              : Array.isArray(params[filterOption.key])
                ? this.getArrayFilterValue(params[filterOption.key])
                : params[filterOption.key];
          }

          const updatedValue =
            (filterOption.isCheckbox || filterOption.isMultiSelect) &&
            !Array.isArray(value)
              ? [value]
              : value;
          this.filterForm?.get(filterOption.key)?.setValue(updatedValue);
          if (filterOption.isAutocomplete) {
            this.prefillAutocompleteOption(filterOption);
          }
        } else {
          this.filterForm?.get(filterOption.key)?.setValue(null);
        }
      });
      //patch sort option
      this.sortByOptions.forEach((sortOption: Option) => {
        if (params['sort'] && sortOption.key === params['sort']) {
          this.sortBy = sortOption.key;
        }
      });
      // Only navigate on initial load to seed SearchService state.
      // On subsequent basicParams changes (tab switches), just sync UI — the parent already handles navigation.
      if (shouldNavigate) {
        this.setParamsToRoute(false);
      }
    });
  }

  private parseDateRange(dateRange: string): Date[] {
    if (!dateRange) {
      return [];
    }
    const dates = dateRange
      .split(',')
      .filter((dateStr) => !!dateStr)
      .map((dateStr) => this.parseIsoDate(dateStr))
      .filter((date) => !Number.isNaN(date.getTime()));
    if (dates.length === 2) {
      return dates;
    }
    if (dates.length === 1) {
      return [dates[0]];
    }
    return [];
  }

  private getNormalizedFilterFormValue(): Record<string, any> {
    const formValue = { ...(this.filterForm?.value || {}) };
    this.filterByOptions.forEach((filterOption: FilterOption) => {
      if (!filterOption.isNumberRange) {
        return;
      }
      const minKey = this.getNumberRangeMinKey(filterOption);
      const maxKey = this.getNumberRangeMaxKey(filterOption);
      formValue[minKey] = this.parseNumberRangeValue(formValue[minKey]);
      formValue[maxKey] = this.parseNumberRangeValue(formValue[maxKey]);
    });
    return formValue;
  }

  private getNumberRangeMinKey(filterOption: FilterOption): string {
    return filterOption.minKey || `${filterOption.key}_min`;
  }

  private getNumberRangeMaxKey(filterOption: FilterOption): string {
    return filterOption.maxKey || `${filterOption.key}_max`;
  }

  private parseNumberRangeValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseIsoDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  }

  private prefillAutocompleteOption(filterOption: FilterOption): void {
    const selectedValue = this.filterForm?.get(filterOption.key)?.value;
    if (!selectedValue || !filterOption.autocompleteUrl) return;
    this.http
      .getReq(filterOption.autocompleteUrl, { params: { length: 10000 } })
      .subscribe((response: ApiResponse) => {
        const desiredOption = response.data?.find(
          (item: any) =>
            item[filterOption?.optionValue ?? 'id'] === selectedValue
        );
        filterOption.options = [desiredOption];
      });
  }

  ngOnDestroy(): void {
    this.searchService.clearParams();
  }
}
